import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, saveToken, clearToken } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [status, setStatus] = useState("checking"); // checking | authed | anon

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            setStatus("authed");
            return data;
        } catch {
            setUser(null);
            setStatus("anon");
            return null;
        }
    }, []);

    useEffect(() => {
        // Skip if returning from OAuth callback
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setStatus("checking");
            return;
        }
        refresh();
    }, [refresh]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        if (data.token) saveToken(data.token);
        setUser(data.user);
        setStatus("authed");
        return data.user;
    };

    const register = async (email, password, name) => {
        const { data } = await api.post("/auth/register", { email, password, name });
        if (data.token) saveToken(data.token);
        setUser(data.user);
        setStatus("authed");
        return data.user;
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {}
        clearToken();
        setUser(null);
        setStatus("anon");
    };

    const setLocal = (u) => setUser(u);

    return (
        <AuthCtx.Provider value={{ user, status, login, register, logout, refresh, setUser: setLocal }}>
            {children}
        </AuthCtx.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthCtx);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
}
